import { initializeApp } from 'firebase/app';
import { getFirestore, collection, getDocs, deleteDoc, doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';

export const firebaseConfig = {
  apiKey: process.env.REACT_APP_APIKEY,

  authDomain: process.env.REACT_APP_AUTHDOMAIN,

  projectId: process.env.REACT_APP_PROYECTID,

  storageBucket: process.env.REACT_APP_STORAGEBUCKET,

  messagingSenderId: process.env.REACT_APP_MESSAGINGSENDERID,

  appId: process.env.REACT_APP_APPID,
};
const app = initializeApp(firebaseConfig);
export const db = getFirestore(app);

export interface FirestoreTimestampLike {
  seconds: number;
  nanoseconds?: number;
}

export interface UserSkillRecord {
  Materia: string;
  Progress: number;
}

export interface UserAchievementRecord {
  Name: string;
  Description: string;
  Status: string;
  Icon?: unknown;
}

export interface UserNotificationRecord {
  Title: string;
  Description: string;
  Checked: boolean;
  Icon?: unknown;
  Date?: FirestoreTimestampLike | null;
}

//USUARIOS
//Create a template para los Usuarios
export interface Task {
  id: string;
  Nombre: string;
  Email: string;
  Rol: string;
  Nivel: number;
  CurrentExp: number;
  Skill: UserSkillRecord[];
  Materias: unknown[];
  PortraitImg: string;
  Puntos: number;
  Recompensas: unknown[];
  Logros: UserAchievementRecord[];
  Items: unknown[];
  Notifications: UserNotificationRecord[];
  Date?: FirestoreTimestampLike | Date | null;
}

export interface NewsTask {
  id: string;
  Titulo: string;
  Subtitle: string;
  Cuerpo: string;
  Imagen: string;
}

const asString = (value: unknown, fallback = ""): string =>
  typeof value === "string" ? value : fallback;

const asNumber = (value: unknown, fallback = 0): number =>
  typeof value === "number" && Number.isFinite(value) ? value : fallback;

const asArray = <T = unknown>(value: unknown): T[] =>
  Array.isArray(value) ? (value as T[]) : [];

const normalizeSkillRecord = (value: unknown): UserSkillRecord => ({
  Materia: asString((value as { Materia?: unknown })?.Materia, "Sin materia"),
  Progress: asNumber((value as { Progress?: unknown })?.Progress),
});

const normalizeAchievementRecord = (value: unknown): UserAchievementRecord => ({
  Name: asString((value as { Name?: unknown })?.Name, "Logro"),
  Description: asString((value as { Description?: unknown })?.Description),
  Status: asString((value as { Status?: unknown })?.Status, "Pendiente"),
  Icon: (value as { Icon?: unknown })?.Icon,
});

const normalizeNotificationRecord = (value: unknown): UserNotificationRecord => ({
  Title: asString((value as { Title?: unknown })?.Title, "Notificación"),
  Description: asString((value as { Description?: unknown })?.Description),
  Checked: Boolean((value as { Checked?: unknown })?.Checked),
  Icon: (value as { Icon?: unknown })?.Icon,
  Date: ((value as { Date?: FirestoreTimestampLike | null })?.Date ?? null),
});

const normalizeUserRecord = (id: string, value: unknown): Task => ({
  id,
  Nombre: asString((value as { Nombre?: unknown })?.Nombre, "Usuario"),
  Email: asString(
    (value as { Email?: unknown; email?: unknown })?.Email ??
    (value as { email?: unknown })?.email,
  ),
  Rol: asString(
    (value as { Rol?: unknown; rol?: unknown })?.Rol ??
    (value as { rol?: unknown })?.rol,
    "regular",
  ),
  Nivel: asNumber(
    (value as { Nivel?: unknown; nivel?: unknown })?.Nivel ??
    (value as { nivel?: unknown })?.nivel,
  ),
  CurrentExp: asNumber(
    (value as { CurrentExp?: unknown; currentExp?: unknown })?.CurrentExp ??
    (value as { currentExp?: unknown })?.currentExp,
  ),
  Skill: asArray((value as { Skill?: unknown })?.Skill).map(normalizeSkillRecord),
  Materias: asArray((value as { Materias?: unknown; materias?: unknown })?.Materias ?? (value as { materias?: unknown })?.materias),
  PortraitImg: asString(
    (value as { PortraitImg?: unknown; portraitimg?: unknown })?.PortraitImg ??
    (value as { portraitimg?: unknown })?.portraitimg,
  ),
  Puntos: asNumber(
    (value as { Puntos?: unknown; puntos?: unknown })?.Puntos ??
    (value as { puntos?: unknown })?.puntos,
  ),
  Recompensas: asArray(
    (value as { Recompensas?: unknown; recompensas?: unknown })?.Recompensas ??
    (value as { recompensas?: unknown })?.recompensas,
  ),
  Logros: asArray((value as { Logros?: unknown; logros?: unknown })?.Logros ?? (value as { logros?: unknown })?.logros).map(
    normalizeAchievementRecord,
  ),
  Items: asArray(
    (value as { Items?: unknown; items?: unknown })?.Items ??
    (value as { items?: unknown })?.items,
  ),
  Notifications: asArray((value as { Notifications?: unknown })?.Notifications).map(
    normalizeNotificationRecord,
  ),
  Date:
    ((value as { Date?: FirestoreTimestampLike | Date | null })?.Date ?? null),
});

const normalizeNewsRecord = (id: string, value: unknown): NewsTask => ({
  id,
  Titulo: asString((value as { Titulo?: unknown; Title?: unknown })?.Titulo ?? (value as { Title?: unknown })?.Title, "Sin título"),
  Subtitle: asString(
    (value as { Subtitle?: unknown; subtitle?: unknown; Subtitulo?: unknown })?.Subtitle ??
    (value as { subtitle?: unknown; Subtitulo?: unknown })?.subtitle ??
    (value as { Subtitulo?: unknown })?.Subtitulo,
  ),
  Cuerpo: asString((value as { Cuerpo?: unknown; Description?: unknown })?.Cuerpo ?? (value as { Description?: unknown })?.Description),
  Imagen: asString((value as { Imagen?: unknown; Image?: unknown })?.Imagen ?? (value as { Image?: unknown })?.Image),
});

//Crear nueva entry en FireStore
export async function CreateNewEntry(nombre: string, email: string, nivel: number, currentExp: number, id: string) {
  try {
    await setDoc(doc(db, "Usuarios", id), {
      Nombre: nombre,
      Email: email,
      Rol: "regular",
      Nivel: nivel,
      CurrentExp: currentExp,
      Skill: "",
      Materias: [],
      PortraitImg: "",
      Puntos: 0,
      Recompensas: [],
      Logros: [],
      Items: [],
      Date: new Date(),
    })
  } catch (error) {
    console.log(error);
  };
}

//Actualizar una entry en Firestore
export async function UpdateEntry(id: string, nombre: string, email: string, rol: string, nivel: number, currentExp: number, skill: string,
  materias: object, portraitimg: string, puntos: number, recompensas: string, logros: string, items: string) {
  try {
    await updateDoc(doc(db, "Usuarios", id),
      {
        Nombre: nombre,
        Email: email,
        Rol: rol,
        Nivel: nivel,
        CurrentExp: currentExp,
        Skill: skill,
        Materias: materias,
        PortraitImg: portraitimg,
        Puntos: puntos,
        Recompensas: recompensas,
        Logros: logros,
        Items: items,
      }
    )
  } catch (error) {
    console.log(error)
  }
}

//Conseguir todas las Entries de la DB
export async function GetAllUsers() {
  try {
    const querySnapShot = await getDocs(collection(db, "Usuarios"));
    return querySnapShot.docs.map((entry) => normalizeUserRecord(entry.id, entry.data()))
  } catch (error) {
    console.log(error)
    return []
  }
}

//Conseguir la informacion de UN solo usuario por ID
export const GetOneUser = async (id: string): Promise<Task | null> => {
  try {
    const querySnapShot = await getDoc(doc(db, "Usuarios", id));
    if (querySnapShot.exists()) {
      const userRecord = normalizeUserRecord(querySnapShot.id, querySnapShot.data());
      localStorage.setItem(id, JSON.stringify(userRecord))
      return userRecord;
    } else {
      return null
    }
  } catch (error) {
    console.log(error)
    throw error
  }
}

//Eliminar una entry en la DB
export async function DeleteUser(id: string) {
  try {
    deleteDoc(doc(db, "Usuarios", id))
  } catch (error) {
    console.log(error)
  }

}

//EVENTOS
export interface EventoTask {
  id: string,
  Nombre: string,
  Puntos: number,
  Imagen: string,
}
//Crear nueva entry en FireStore
export async function CreateNewEvent(nombre: string, puntos: number, img: string, id: string) {
  try {
    await setDoc(doc(db, "Eventos", id), {
      Nombre: nombre,
      Puntos: puntos,
      Imagen: img,
    })
  } catch (error) {
    console.log(error);
  };
}

//Conseguir todos los Eventos de la DB
export async function GetAllEvents() {
  try {
    const querySnapShot = await getDocs(collection(db, "Eventos"));
    return querySnapShot.docs.map(doc => ({ id: doc.id, ...doc.data() } as EventoTask))
  } catch (error) {
    console.log(error)
    return []
  }
}

//Conseguir la informacion de UN solo Evento por ID
export const GetOneEvent = async (id: string): Promise<EventoTask | null> => {
  try {
    const querySnapShot = await getDoc(doc(db, "Eventos", id));
    if (querySnapShot.exists()) {
      return { id: querySnapShot.id, ...querySnapShot.data() } as EventoTask;
    } else {
      return null
    }
  } catch (error) {
    console.log(error)
    throw error
  }
}

//Eliminar una entry en la DB
export async function DeleteEvent(id: string) {
  try {
    deleteDoc(doc(db, "Eventos", id))
  } catch (error) {
    console.log(error)
  }
}

//Conseguir todos las Noticias de la DB
export async function GetAllNews() {
  try {
    const querySnapShot = await getDocs(collection(db, "Noticias"));
    return querySnapShot.docs.map((entry) => normalizeNewsRecord(entry.id, entry.data()))
  } catch (error) {
    console.log(error)
    return []
  }
}

//Editado el 16/4
//Debido a la falta de tiempo y no ser capaz de decifrar el fucionamiento del nuevo codigo..
//realizo codigo rebundante como solucion temporal.

//Actualizar la PortraitImg de una entry
export async function UpdateEntryImg(id: string, portraitimg: string) {
  try {
    const DocRef = await updateDoc(doc(db, "Usuarios", id),
      {
        PortraitImg: portraitimg,
      }
    )
  } catch (error) {
    console.log(error)
  }
}
//Actualizar las Recompensas de una entry
export async function UpdateEntryRewards(id: string, recompensas: string) {
  try {
    const DocRef = await updateDoc(doc(db, "Usuarios", id),
      {
        Recompensas: recompensas
      }
    )
  } catch (error) {
    console.log(error)
  }
}
//Actualizar los Niveles de una entry
export async function UpdateEntryLevels(id: string, nivel: number) {
  try {
    const DocRef = await updateDoc(doc(db, "Usuarios", id),
      {
        Nivel: nivel,
      }
    )
  } catch (error) {
    console.log(error)
  }
}
//Actualizar las Notificaciones de una entry
export async function UpdateEntryNotifications(id: string, notifications: any) {
  try {
    const DocRef = await updateDoc(doc(db, "Usuarios", id),
      {
        Notifications: notifications,
      }
    )
  } catch (error) {
    console.log(error)
  }
}